import React, { useContext, useEffect, useMemo, useState } from 'react';
import LoaderSpinner from '../../../SystemDesign/LoaderSpinner';
import { Box, Typography, useTheme, Button, useMediaQuery } from '@mui/material';
import { tokens } from '../../../theme';
import AddIcon from '@mui/icons-material/Add';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import SearchInput from '../../../SystemDesign/SearchInput';
import ListView from '../../../SystemDesign/ListViewV2';
import ViewToggle from '../../../SystemDesign/ViewToggle';
import PosteCard from '../../../SystemDesign/PosteCard'; // keep component but used for Position rendering
import CardMenuActions from '../../../SystemDesign/CardMenuActions';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { AuthContext } from '../../../Context/AuthContext';
import { formatKey, formattedDate, isEmpty } from '../../../Utils/Functions';
import DateInTable from '../../../SystemDesign/DateInTable';
import ListViewMenuActions from '../../../SystemDesign/ListViewMenuActions';
import { useGetPostesQuery } from '../../../services/SkillsManagement';
import AddPoste from './AddPoste';
import UpdatePoste from './UpdatePoste';
import DeletePoste from './DeletePoste';
import SeeMorePoste from './SeeMorePoste';
import PosteCardView from './PosteCard';
import PaginationUI from '../../../SystemDesign/PaginationUI';
import AssignPosteToDiscipline from './AssignPosteToDiscipline';
import AddRequirementsSkills from '../../SkillRequirements/AddRequirementsSkills';
import { SubTitleScreen, TitleScreen } from '../../../SystemDesign/Styles';

const PostesList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { ability } = useContext(AuthContext);
  const [columnFilters, setColumnFilters] = useState([]);
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [searchTerm, setSearchTerm] = useState('');

  // Responsive breakpoints
  const isMdOrLarger = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const isSmOrLarger = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const [queryParams, setQueryParams] = useState(() => {
    const savedRowsPerPage = localStorage.getItem(`positions-rowsPerPage`);
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    return {
      filters: '',
      sorting: '',
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage }),
    };
  });

  const { data, error, isLoading, isFetching } = useGetPostesQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  // Extract postes directly from RTK Query data
  const postes = useMemo(() => {
    if (data?.postes) {
      return data.postes;
    }
    return [];
  }, [data]);

  const actionsList = useMemo(
    () => [
      { component: SeeMorePoste, key: 'see-more' },
      ...(ability?.can('update', 'Position') ? [{ component: UpdatePoste, key: 'update' }] : []),
      ...(ability?.can('manage', 'Position') ? [{ component: AddRequirementsSkills, key: 'requirements' }] : []),
      ...(ability?.can('manage', 'Position') ? [{ component: AssignPosteToDiscipline, key: 'assign-position' }] : []),
      ...(ability?.can('delete', 'Position') ? [{ component: DeletePoste, key: 'delete' }] : []),
    ],
    [ability]
  );

  const columns = useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => info.getValue(),
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...(isSmOrLarger
        ? [
            {
              id: 'users',
              accessorKey: 'users',
              header: 'Users',
              cell: (info) => {
                const users = info.getValue();
                const count = Array.isArray(users) ? users.length : 0;
                return `${count} user${count !== 1 ? 's' : ''}`;
              },
              enableColumnFilter: false,
              enableSorting: false,
            },
            {
              id: 'skillRequirements',
              accessorKey: 'skillRequirements',
              header: 'Skills',
              cell: (info) => {
                const skills = info.getValue();
                const count = Array.isArray(skills) ? skills.length : 0;
                return `${count} skill${count !== 1 ? 's' : ''}`;
              },
              enableColumnFilter: false,
              enableSorting: false,
            },
            {
              id: 'createdBy.username',
              accessorKey: 'createdBy',
              header: 'Created by',
              cell: (info) => {
                const createdBy = info.getValue();
                return isEmpty(createdBy) ? 'Unknown' : createdBy?.username;
              },
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      ...(isMdOrLarger
        ? [
            {
              id: 'createdAt',
              accessorKey: 'createdAt',
              header: 'Created at',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      ...(isMdOrLarger
        ? [
            {
              id: 'updatedAt',
              accessorKey: 'updatedAt',
              header: 'Updated at',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'actions',
        header: '',
        size: 100,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <ListViewMenuActions>
            {actionsList.map(({ component: Component, key }) => (
              <Component key={key} row={row} actionKey={key} />
            ))}
          </ListViewMenuActions>
        ),
      },
    ],
    [actionsList, isMdOrLarger, isSmOrLarger]
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (!value) applyFilters(null, null, null, '');
  };

  const handleSearchSubmit = (value) => applyFilters(null, null, null, value);

  const applyFilters = (updatedFilters = columnFilters, paginationInfo = null, sortingInfo = null, searchValue = null) => {
    const filtersArray = updatedFilters || [];
    const formattedFilters = filtersArray.reduce((acc, filter) => {
      if (typeof filter === 'object' && !Array.isArray(filter)) {
        const [key, values] = Object.entries(filter)[0];
        if (Array.isArray(values)) {
          const nonEmptyValues = values.filter((val) => val !== '');
          if (nonEmptyValues.length > 0) acc[key] = nonEmptyValues.map((val) => String(val));
        } else if (values !== '') acc[key] = [String(values)];
      }
      return acc;
    }, {});

    let rowsPerPage = paginationInfo?.rowsPerPage;
    if (!rowsPerPage) {
      const saved = localStorage.getItem(`positions-rowsPerPage`);
      rowsPerPage = saved !== null ? parseInt(saved, 10) : 10;
    } else {
      localStorage.setItem(`positions-rowsPerPage`, rowsPerPage.toString());
    }

    if (searchValue !== null && searchValue.trim() !== '') {
      formattedFilters.name = [searchValue];
    }

    setQueryParams((prev) => ({
      ...prev,
      filters: isEmpty(formattedFilters) ? '' : JSON.stringify(formattedFilters),
      pagination: JSON.stringify({
        currentPage: paginationInfo?.currentPage || 1,
        rowsPerPage,
      }),
      sorting: sortingInfo ? JSON.stringify(sortingInfo) : prev.sorting,
    }));
  };

  useEffect(() => {
    if (error) {
      setInfoAlert({ severity: 'error', message: error.data?.message || 'An error occurred' });
      setOpenSnackBar(true);
    }
  }, [error, setInfoAlert, setOpenSnackBar]);


  return (
    <Box p={3}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <div>
            <Typography sx={TitleScreen(colors)}>Positions List</Typography>
            <Typography sx={SubTitleScreen(colors)}>Manage user positions and view associated details.</Typography>
          </div>
          <div>
            <Button startIcon={<AddIcon />} onClick={() => setIsOpen(true)} variant="contained" color="primary">
              Add Position
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '2rem' }}>
          <div style={{ flexGrow: 1 }}>
            <SearchInput
              placeholder="Search positions by name..."
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearchSubmit}
            />
          </div>
          <ViewToggle value={viewMode} onChange={(value) => setViewMode(value)} />
        </div>

        <MuiDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          icon={<AddIcon />}
          title="Create New Position"
          subtitle="Add a new position to the system"
          content={<AddPoste open={isOpen} onClose={() => setIsOpen(false)} />}
          maxWidth="sm"
        />

        {isLoading ? (
          <LoaderSpinner loading={isLoading || isFetching} />
        ) : viewMode === 'table' ? (
          <ListView
            dispatchData={postes}
            columns={columns}
            tableName={'positions'}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            applyFilters={applyFilters}
            pagination={data?.pagination}
            setQueryParams={setQueryParams}
            queryParams={queryParams}
            showColumnManager={false}
          />
        ) : (
          <>
            <div className="card-view-container">
              <div>
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ flexGrow: 1 }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '24px',
                      }}
                    >
                      {postes.map((poste, index) => (
                        <div key={poste.posteId || index}>
                          <div style={{ position: 'relative' }}>
                            <PosteCard poste={poste} index={index} />
                            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                              <CardMenuActions>
                                {actionsList.map(({ component: Component, key }) => (
                                  <Component key={key} row={{ original: poste }} actionKey={key} />
                                ))}
                              </CardMenuActions>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <PaginationUI
              paginationInfo={{
                currentPage: JSON.parse(queryParams.pagination || '{}').currentPage || 1,
                rowsPerPage: JSON.parse(queryParams.pagination || '{}').rowsPerPage || 12,
                total: data?.pagination?.total || postes.length || 0,
              }}
              isLoading={isLoading || isFetching}
              onPageChange={(page, rowsPerPage) => applyFilters(null, { currentPage: page, rowsPerPage })}
              onRowsPerPageChange={(rowsPerPage) => applyFilters(null, { currentPage: 1, rowsPerPage })}
            />
          </>
        )}
      </div>
    </Box>
  );
};

export default PostesList;
